/**
 * Reading a built robot's joint axes back as a left/right consistency check.
 *
 * A legged robot is only as symmetric as its servos are mounted. Two hips
 * that look identical can turn opposite ways, and that only shows up as a
 * limp once the gait runs. The axes are all on screen in axis overview
 * mode, but "are these two the same" is a question about numbers, so this
 * pairs the joints across the robot's mirror plane and says what relation
 * each pair actually has.
 *
 * The maths that decides it: reflecting through a plane is an improper
 * transform, so a rotation axis -- a pseudovector -- does not simply carry
 * over. Mirroring a joint that spins about `a` gives a joint that spins
 * about `-M a`, where `M` is the reflection. So for a pair to move
 * mirror-symmetrically under the same positive command, the right axis
 * must equal `-M a_left`; if it equals `+M a_left` instead, the two
 * servos share one mounting orientation and a positive command turns both
 * the same way in the world. Anything else means the axes are not even
 * collinear once mirrored, which is a build error rather than a choice.
 *
 * Everything here is pure so it can be tested without a scene; the
 * overlay and the panel both read the same result.
 */

import type { JointAxisData } from "../types";

export type Vec3 = [number, number, number];

/** Which world direction an axis lines up with, once a tolerance is allowed. */
export type AxisDirection = "+X" | "-X" | "+Y" | "-Y" | "+Z" | "-Z" | "oblique";

/**
 * How a left/right pair relates.
 *
 * - `mirror` — mirror-symmetric: one positive command moves both sides
 *   symmetrically (left knee and right knee bend together).
 * - `parallel` — same mounting orientation on both sides: one positive
 *   command turns both the same way in the world.
 * - `skew` — the axes are not collinear after mirroring at all. Almost
 *   always a servo mounted at the wrong angle.
 */
export type MirrorRelation = "mirror" | "parallel" | "skew";

export interface AxisPair {
  /** The joint on the positive side of the mirror normal (+Y is ROS left). */
  left: JointAxisData;
  right: JointAxisData;
  relation: MirrorRelation;
  /**
   * How far the right axis is from the nearer of the two clean
   * conventions, in degrees. Zero for a perfectly built pair.
   */
  deviationDeg: number;
  /** Distance between the right joint and the mirror image of the left one, in metres. */
  positionErrorM: number;
}

export interface CenterJoint {
  joint: JointAxisData;
  /**
   * Whether moving it keeps the robot left/right symmetric.
   *
   * True when the axis lies along the mirror normal -- a waist pitch on a
   * Z-up, X-forward robot. A centre yaw or roll is asymmetric by nature,
   * which is normal, so this is reported rather than warned about.
   */
  symmetric: boolean;
}

export interface AxisAudit {
  pairs: AxisPair[];
  center: CenterJoint[];
  /** Off-plane joints with no partner on the other side. */
  unpaired: JointAxisData[];
  /** Pairs whose axes are not collinear after mirroring -- the ones worth fixing. */
  skewed: AxisPair[];
  /**
   * Whether the robot mixes the two clean conventions.
   *
   * Not an error -- plenty of designs mirror the hips and share the knee
   * orientation -- but worth surfacing, because a single odd one out in
   * an otherwise consistent robot is usually a mistake.
   */
  mixedConventions: boolean;
}

export interface AxisAuditOptions {
  /** Normal of the plane the robot should be symmetric about. Default `[0, 1, 0]`. */
  mirrorNormal?: Vec3;
  /** How far an axis may sit from a convention before the pair counts as skewed. */
  angleToleranceDeg?: number;
  /** How close to the plane a joint has to be to count as a centre joint. */
  planeToleranceM?: number;
  /** How far a joint may sit from its partner's mirror image and still pair. */
  pairToleranceM?: number;
}

const DEFAULT_MIRROR_NORMAL: Vec3 = [0, 1, 0];
const DEFAULT_ANGLE_TOLERANCE_DEG = 10;
const DEFAULT_PLANE_TOLERANCE_M = 0.005;
/** Floor for the pairing tolerance; scaled up for a large robot, see `pairTolerance`. */
const MIN_PAIR_TOLERANCE_M = 0.02;
const PAIR_TOLERANCE_RATIO = 0.05;

/** Tokens that only say which side a joint is on, and so must not block a match. */
const SIDE_TOKENS = new Set(["l", "r", "left", "right", "lh", "rh", "lf", "rf"]);

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function scale(a: Vec3, k: number): Vec3 {
  return [a[0] * k, a[1] * k, a[2] * k];
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function length(a: Vec3): number {
  return Math.sqrt(dot(a, a));
}

export function normalize(a: Vec3): Vec3 {
  const norm = length(a);
  if (norm < 1e-12) {
    return [0, 0, 1];
  }
  return scale(a, 1 / norm);
}

/** Reflect a point or a proper vector through the plane through the origin with this normal. */
export function reflect(v: Vec3, normal: Vec3): Vec3 {
  const n = normalize(normal);
  const twiceProjection = 2 * dot(v, n);
  return sub(v, scale(n, twiceProjection));
}

/**
 * The axis of the mirror image of a joint that turns about `axis`.
 *
 * The extra negation is what makes this different from `reflect`: a
 * rotation axis is a pseudovector, so an improper transform flips it.
 */
export function mirrorRotationAxis(axis: Vec3, normal: Vec3): Vec3 {
  return scale(reflect(axis, normal), -1);
}

/** Angle between two directions in degrees, ignoring nothing -- sign included. */
export function angleBetweenDeg(a: Vec3, b: Vec3): number {
  const cosine = Math.min(1, Math.max(-1, dot(normalize(a), normalize(b))));
  return (Math.acos(cosine) * 180) / Math.PI;
}

/**
 * Which world direction an axis points along.
 *
 * Returns `oblique` when it is not within `toleranceDeg` of any of the six
 * signed world axes -- which is itself informative, since a servo that
 * should be a clean pitch axis and reads oblique is mounted crooked.
 */
export function axisDirection(axis: Vec3, toleranceDeg = DEFAULT_ANGLE_TOLERANCE_DEG): AxisDirection {
  const candidates: [AxisDirection, Vec3][] = [
    ["+X", [1, 0, 0]],
    ["-X", [-1, 0, 0]],
    ["+Y", [0, 1, 0]],
    ["-Y", [0, -1, 0]],
    ["+Z", [0, 0, 1]],
    ["-Z", [0, 0, -1]],
  ];
  for (const [label, direction] of candidates) {
    if (angleBetweenDeg(axis, direction) <= toleranceDeg) {
      return label;
    }
  }
  return "oblique";
}

/**
 * Strip the side out of a joint name so the two sides of a pair can match.
 *
 * `leg_left_1/servo_pitch` and `leg_right_1/servo_pitch` both come back as
 * `leg 1 servo pitch`. Used only to prefer one candidate over another when
 * both are geometrically plausible; a robot whose modules carry generated
 * names still pairs on position alone.
 */
export function sideAgnosticName(name: string): string {
  return name
    .split(/[_/\-.\s]+/)
    .filter((token) => token.length > 0 && !SIDE_TOKENS.has(token.toLowerCase()))
    .join(" ")
    .toLowerCase();
}

/**
 * Pairing tolerance, widened for a robot that is simply bigger.
 *
 * A humanoid the size of a desk and one the size of a hand cannot share a
 * fixed millimetre budget, so this takes a fraction of the spread of the
 * joints themselves, with a floor for the small case.
 */
function pairTolerance(axes: JointAxisData[], override?: number): number {
  if (override !== undefined) {
    return override;
  }
  if (axes.length === 0) {
    return MIN_PAIR_TOLERANCE_M;
  }
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const axis of axes) {
    for (let i = 0; i < 3; i++) {
      min[i] = Math.min(min[i], axis.position[i]);
      max[i] = Math.max(max[i], axis.position[i]);
    }
  }
  const extent = length(sub(max, min));
  return Math.max(MIN_PAIR_TOLERANCE_M, extent * PAIR_TOLERANCE_RATIO);
}

function classify(
  left: JointAxisData,
  right: JointAxisData,
  normal: Vec3,
  angleToleranceDeg: number
): { relation: MirrorRelation; deviationDeg: number } {
  const mirrored = mirrorRotationAxis(left.axis, normal);
  const toMirror = angleBetweenDeg(right.axis, mirrored);
  // The parallel convention is the exact opposite axis, so its deviation is
  // whatever is left of a straight angle.
  const toParallel = 180 - toMirror;
  if (toMirror <= angleToleranceDeg) {
    return { relation: "mirror", deviationDeg: toMirror };
  }
  if (toParallel <= angleToleranceDeg) {
    return { relation: "parallel", deviationDeg: toParallel };
  }
  return { relation: "skew", deviationDeg: Math.min(toMirror, toParallel) };
}

/**
 * Pair a robot's joints across its mirror plane and report each pair's relation.
 *
 * Parameters
 * ----------
 * axes
 *     Every movable joint's world-frame axis, as the server sends them.
 * options
 *     See `AxisAuditOptions`; the defaults assume a Z-up, X-forward robot
 *     that should be symmetric about the XZ plane.
 *
 * Notes
 * -----
 * The pairing is geometric, so it reads the robot *in its current pose*.
 * Pose one leg and not the other and the pair stops matching -- which the
 * panel says out loud, since the alternative would be to silently report
 * a build problem that is really just a slider left off centre.
 */
export function auditJointAxes(axes: JointAxisData[], options: AxisAuditOptions = {}): AxisAudit {
  const normal = normalize(options.mirrorNormal ?? DEFAULT_MIRROR_NORMAL);
  const angleToleranceDeg = options.angleToleranceDeg ?? DEFAULT_ANGLE_TOLERANCE_DEG;
  const planeToleranceM = options.planeToleranceM ?? DEFAULT_PLANE_TOLERANCE_M;
  const posTolerance = pairTolerance(axes, options.pairToleranceM);

  const center: CenterJoint[] = [];
  const offPlane: JointAxisData[] = [];
  for (const joint of axes) {
    const side = dot(joint.position, normal);
    if (Math.abs(side) <= planeToleranceM) {
      center.push({
        joint,
        symmetric: angleBetweenDeg(joint.axis, normal) <= angleToleranceDeg
          || angleBetweenDeg(joint.axis, scale(normal, -1)) <= angleToleranceDeg,
      });
    } else {
      offPlane.push(joint);
    }
  }

  // Every plausible pairing, cheapest first: a name match wins over a
  // stranger that happens to sit at the same place, and among equals the
  // closest mirror image wins.
  const candidates: { left: JointAxisData; right: JointAxisData; distance: number; nameMatch: boolean }[] = [];
  for (let i = 0; i < offPlane.length; i++) {
    for (let j = i + 1; j < offPlane.length; j++) {
      const a = offPlane[i];
      const b = offPlane[j];
      if (a.joint_type !== b.joint_type) {
        continue;
      }
      const sideA = dot(a.position, normal);
      const sideB = dot(b.position, normal);
      if (sideA * sideB > 0) {
        continue;
      }
      const distance = length(sub(b.position, reflect(a.position, normal)));
      if (distance > posTolerance) {
        continue;
      }
      const left = sideA > 0 ? a : b;
      const right = sideA > 0 ? b : a;
      candidates.push({
        left,
        right,
        distance,
        nameMatch: sideAgnosticName(a.name) === sideAgnosticName(b.name),
      });
    }
  }
  candidates.sort((a, b) => {
    if (a.nameMatch !== b.nameMatch) {
      return a.nameMatch ? -1 : 1;
    }
    return a.distance - b.distance;
  });

  const taken = new Set<string>();
  const pairs: AxisPair[] = [];
  for (const candidate of candidates) {
    if (taken.has(candidate.left.name) || taken.has(candidate.right.name)) {
      continue;
    }
    taken.add(candidate.left.name);
    taken.add(candidate.right.name);
    const { relation, deviationDeg } = classify(candidate.left, candidate.right, normal, angleToleranceDeg);
    pairs.push({
      left: candidate.left,
      right: candidate.right,
      relation,
      deviationDeg,
      positionErrorM: candidate.distance,
    });
  }

  const unpaired = offPlane.filter((joint) => !taken.has(joint.name));
  const skewed = pairs.filter((pair) => pair.relation === "skew");
  const mixedConventions =
    pairs.some((pair) => pair.relation === "mirror") && pairs.some((pair) => pair.relation === "parallel");

  return { pairs, center, unpaired, skewed, mixedConventions };
}
